
import { useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Lead } from '@/types/lead';
import { getWebSearchData, getWebSearchStatus, enrichLeadWithWebSearch } from '@/services/webSearchService';
import { useWebSearchState } from './useWebSearchState';
import { useWebSearchProgress } from './useWebSearchProgress';
import { useWebSearchRefresh } from './useWebSearchRefresh';
import { useWebSearchQueue } from './useWebSearchQueue';

export const useWebSearch = (lead: Lead, onDataUpdated?: () => void) => {
  const { toast } = useToast();
  const state = useWebSearchState();
  const { calculateProgress } = useWebSearchProgress();
  const { startStatusRefresh } = useWebSearchRefresh(
    lead,
    state.setSearchData,
    state.setSearchStatus,
    state.setProgressValue,
    state.setLastUpdated,
    state.setQueuePosition,
    state.refreshInterval,
    state.setRefreshInterval,
    calculateProgress,
    onDataUpdated
  );
  const { handleProcessQueue } = useWebSearchQueue(
    state.searchStatus,
    startStatusRefresh,
    state.setIsProcessing,
    lead.id
  );
  
  const autoFixRanRef = useRef(false);

  const handleRefresh = async () => {
    state.setIsLoading(true);
    try {
      state.setSearchStatus('pending');
      state.setProgressValue(calculateProgress('pending'));
      
      await enrichLeadWithWebSearch(lead);
      startStatusRefresh();
      
      toast({
        title: "Web search started",
        description: `Retrieving web information for ${lead.name}`,
      });
    } catch (error) {
      console.error('Error refreshing web search data:', error);
      state.setSearchStatus('failed');
      state.setProgressValue(calculateProgress('failed'));
      
      toast({
        variant: "destructive",
        title: "Web search failed",
        description: "An error occurred while starting web information retrieval.",
      });
    } finally {
      state.setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      state.setIsInitialLoading(true);

      try {
        const data = await getWebSearchData(lead.id);
        state.setSearchData(data);

        const status = await getWebSearchStatus(lead.id);

        const liSource = data?.linkedIn?.dataSource;
        if (liSource === 'verified_linkedin') {
          state.setSearchStatus('completed');
          state.setProgressValue(calculateProgress('completed'));
        } else {
          state.setSearchStatus(status?.status || null);
          state.setProgressValue(calculateProgress(status?.status || null));
        }

        // Auto re-verify LinkedIn if we have a URL but it's not strictly verified
        if (!autoFixRanRef.current && 
            data?.linkedIn?.companyPageUrl && 
            data?.linkedIn?.dataSource !== 'verified_linkedin' &&
            data?.linkedIn?.dataSource !== 'not_found') {
          autoFixRanRef.current = true;
          try {
            await enrichLeadWithWebSearch(lead);
            startStatusRefresh();
          } catch (e) {
            console.error('Auto re-verify LinkedIn failed:', e);
          }
        }

        state.setLastUpdated(status?.updatedAt || null);
        state.setQueuedAt(status?.queuedAt || null);

        if (status?.status === 'pending' && !data) {
          const position = Math.floor(Math.random() * 5) + 1;
          state.setQueuePosition(position);
        }

        const notVerified = liSource !== 'verified_linkedin';
        if ((status?.status === 'pending' || status?.status === 'processing') && notVerified) {
          startStatusRefresh();
        }
      } catch (error) {
        console.error('Error fetching web search data:', error);
      } finally {
        state.setIsInitialLoading(false);
      }
    };

    fetchData();

    return () => {
      if (state.refreshInterval) {
        clearInterval(state.refreshInterval);
      }
    };
  }, [lead.id]);
  
  return {
    searchData: state.searchData,
    searchStatus: state.searchStatus,
    lastUpdated: state.lastUpdated,
    queuedAt: state.queuedAt,
    isLoading: state.isLoading,
    isProcessing: state.isProcessing,
    isInitialLoading: state.isInitialLoading,
    progressValue: state.progressValue,
    queuePosition: state.queuePosition,
    handleRefresh,
    handleProcessQueue
  };
};
